import { intersection } from 'lodash'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@apollo/client'
import { useAuth } from '@praxis/component-auth'
// import { itemCommonUtilis } from 'item-component'
import { userSessionTokenQuery } from '../apiItemGraphql/userSession'
import { bannerQuery } from '../apiItemGraphql/banner'
import { setUserProfile, setUserSession } from '../store/userProfile'
import { setFeatureFlag } from '../store/featureFlag'
import { setBanner } from '../store/bannerHeader'
import {
  getFlagInfo,
  getUserInfo,
  userIsInSomeRole,
} from '../globalUtils/commonUtills'
import { mainMenus, dynamicMenus } from '../constants/menu'
import { featureFlagQuery } from '../apiItemGraphql/featureFlag'
import constants from '../constants/constants'

export const useMain = () => {
  const dispatch = useDispatch()
  const auth = useAuth()
  const { session } = auth
  const { loading, error, data } = useQuery(userSessionTokenQuery())
  const getUserProfile = useSelector((state) => {
    return getUserInfo(state)
  })

  //FeatureFlagMap - map to collect the featureFlag value based on which dynamic menus should be shown
  const featureFlagMap = {
    //1. showBulkFlag - flag to read setting for bulk-upload
    showBulkFlag: useSelector((state) => {
      return getFlagInfo(state.featureFlags.details, 'bulk-upload')
    }),
    showIauMfe: useSelector((state) => {
      return getFlagInfo(state.featureFlags.details, 'IAU-task-level-comment')
    }),
  }

  //#region UseEffects
  useEffect(() => {
    dispatch(setUserProfile(data?.userSessionToken?.token))
    if (session) {
      dispatch(setUserSession(session))
    }
  }, [data, dispatch, session])
  const { data: bannerData } = useQuery(bannerQuery())
  const { data: featureData } = useQuery(featureFlagQuery(), {
    skip: !getUserProfile, // only runs once user profile is available
  })

  useEffect(() => {
    dispatch(setBanner(bannerData?.getBannerDetail?.bannerDetails))
  }, [bannerData, dispatch])

  useEffect(() => {
    dispatch(setFeatureFlag(featureData?.featureFlags))
  }, [featureData, dispatch])

  //#endregion

  const bannerHeader = useSelector((state) => state.bannerHeader)

  const hasRole = (userRoles, allowedRoles) => {
    return intersection(userRoles, allowedRoles).length > 0
  }

  /**
   * Returns a filtered and mapped list of dynamic menu objects based on feature flags and domain context.
   * @param {boolean} [isItemHubDomain=false] - If true, uses `itemHubPath` for each menu's path; otherwise, uses `path`.
   * @returns {Array<Object>} Filtered and mapped dynamic menu objects with the appropriate path set.
   */
  const getFilteredDynamicMenus = (isItemHubDomain = false) => {
    return dynamicMenus
      .filter(
        (eachDynamicMenu) => featureFlagMap[eachDynamicMenu.enablementFlag],
      )
      .map((eachDynamicMenu) => {
        let menuSetting = {
          ...eachDynamicMenu,
          path: isItemHubDomain
            ? eachDynamicMenu.itemHubPath
            : eachDynamicMenu.path,
        }
        return menuSetting
      })
  }

  /**
   * Constructs and returns the main menu array based on user profile, roles, and domain context.
   *
   * Filters the mainMenus array to include only enabled features or those accessible by the user's roles.
   * Optionally, for the Itemhub domain, returns only the main menus without dynamic additions.
   * Otherwise, inserts dynamic menus after specified menu items or appends them if no match is found.
   * @param {boolean} [isItemhubDomain=false] - If true, returns only the main menus for the Itemhub domain.
   * @returns {Array<Object>} The constructed array of menu items to be displayed in the UI.
   */
  const constructedMenu = (isItemhubDomain = false) => {
    let menus = mainMenus.filter((feature) => feature.enabled === true)
    if (getUserProfile) {
      const filteredMenu = mainMenus.map((feature) => {
        const userRoles = [
          ...(getUserProfile?.roles ?? []),
          ...(getUserProfile?.itemRoles ?? []),
        ]
        const canViewMenu =
          feature.enabled === true || hasRole(userRoles, feature.enabled)
        return canViewMenu ? feature : null
      })
      menus = filteredMenu.filter((n) => n)
    }
    // For Itemhub exclude menus on the Grouper on sidenav
    // if (itemCommonUtilis.isItemHub) {
    //   const excludedApplications = ['Groups']
    //   return menus.filter(
    //     (menuItem) => !excludedApplications.includes(menuItem.name),
    //   )
    // }

    //Case itemhub - return main menus only
    if (isItemhubDomain) {
      return menus
    }

    //Non-itemhub - insert required dynamic menus
    getFilteredDynamicMenus()?.forEach((eachDynamicMenu) => {
      const insertAfterIndex = menus.findIndex(
        (route) => route.name === eachDynamicMenu.insertAfter,
      )
      if (insertAfterIndex !== -1) {
        menus.splice(insertAfterIndex + 1, 0, eachDynamicMenu)
      } else {
        menus.push(eachDynamicMenu)
      }
    })
    return menus
  }

  /**
   * Constructs and returns the filtered dynamic menus.
   * @returns {Array} The filtered list of dynamic menu items.
   */
  const constructDynamicMenu = () => {
    return getFilteredDynamicMenus(true)
  }
  const canUserBulk = userIsInSomeRole(getUserProfile?.roles, [
    constants.USER_ROLES.ADMIN,
    constants.USER_ROLES.VENDOR,
  ])

  return {
    loading,
    error,
    data,
    bannerHeader,
    constructedMenu,
    constructDynamicMenu,
    getUserProfile,
    canUserBulk,
  }
}
